import { useLayoutEffect, useRef, type RefObject } from 'react';

import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';

import { getSpacerHeights } from '../../../utils/PurchaseOrdersUtils';

interface UseVirtualRowsArgs {
    /** Total number of rows in the list, including the ones scrolled out of view. */
    rowCount: number;
    /** Pixel height of the row at the given index, needed up front for every row. */
    getRowHeight: (index: number) => number;
    /** Stable key for the row at the given index. */
    getRowKey: (index: number) => string;
    /**
     * When this value changes the list scrolls back to the top. Pass a key derived from the
     * inputs that reorder or refilter the rows (e.g. a search term and sort), so the browser
     * does not leave the scroll offset stranded past the end of a now-shorter list.
     */
    resetKey?: string;
    /** Rows to render beyond the visible window on each side. @default 5 */
    overscan?: number;
}

interface UseVirtualRowsResult {
    /** Attach to the scrolling element (the one with `overflow: auto`). */
    scrollRef: RefObject<HTMLDivElement | null>;
    /** The rows the virtualiser currently wants rendered. */
    virtualRows: Array<VirtualItem>;
    /** Spacer height standing in for the rows above the rendered window. */
    topSpacerHeight: number;
    /** Spacer height standing in for the rows below the rendered window. */
    bottomSpacerHeight: number;
}

const defaultOverscan = 5;

/**
 * Vertical virtualisation for a list whose rows stay in normal document flow, kept there by two
 * spacer heights rather than absolute positioning so the rows remain free to size horizontally.
 */
const useVirtualRows = ({
    rowCount,
    getRowHeight,
    getRowKey,
    resetKey,
    overscan = defaultOverscan,
}: UseVirtualRowsArgs): UseVirtualRowsResult => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: rowCount,
        getScrollElement: () => scrollRef.current,
        estimateSize: getRowHeight,
        getItemKey: getRowKey,
        overscan,
    });

    const virtualRows = virtualizer.getVirtualItems();
    const { topSpacerHeight, bottomSpacerHeight } = getSpacerHeights({
        virtualRows,
        totalHeight: virtualizer.getTotalSize(),
    });

    useLayoutEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [resetKey]);

    return { scrollRef, virtualRows, topSpacerHeight, bottomSpacerHeight };
};

export { useVirtualRows };
export type { UseVirtualRowsArgs, UseVirtualRowsResult };
